import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';

export interface IClientApiDto {
  id: number;
  code?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  isActive?: boolean;
  companyId?: number;
  ownerUserId?: number | null;
  createdAt?: string;
}

export interface IClientListResponseDto {
  list: IClientApiDto[];
  total: number;
  totalPages: number;
}

export interface IClientListQueryDto {
  page?: number;
  limit?: number;
  search?: string;
}

export interface IInvitationMetadata {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface IInvitationApiDto {
  id: string;
  entityType: string;
  entityId: number;
  email?: string | null;
  state: string;
  channel: string;
  metadata: IInvitationMetadata;
  createdAt: string;
  expiresAt: string;
  approvedAt?: string;
}

export interface IInvitationListResponseDto {
  invitations: IInvitationApiDto[];
  total: number;
}

export interface IInvitationListQueryDto {
  entityType?: string;
  state?: string;
  limit?: number;
  offset?: number;
}

export interface ICreateClientDto {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  type: 'individual' | 'business';
  name?: string;
  language?: string;
}

export interface ICreateInvitationResponseDto {
  invitationId: string;
  state: string;
  expiresAt: string;
}

// Support session interfaces
export interface ISupportSessionDto {
  id: number;
  actorUserId: number | null;
  actorType: 'ADMIN' | 'SYSTEM';
  subjectUserId: number;
  companyId: number;
  reason: string;
  supportMode: 'READ_ONLY' | 'FULL_SUPPORT';
  status: 'ACTIVE' | 'TERMINATED' | 'EXPIRED';
  startedAt: string;
  expiresAt: string;
  endedAt?: string | null;
}

export interface ILatestSupportSessionResponseDto {
  session: ISupportSessionDto | null;
}

export interface ICreateSupportSessionDto {
  subjectUserId: number;
  companyId: number;
  reason: string;
  supportMode: 'READ_ONLY' | 'FULL_SUPPORT';
}

export interface ISupportSessionResponseDto {
  session: ISupportSessionDto;
  code: string;
  codeExpiresAt: string;
}

export interface IIssueSupportCodeResponseDto {
  code: string;
  codeExpiresAt: string;
}

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Clients', 'Invitations', 'SupportSessions'],
  endpoints: build => ({
    getClientList: build.query<IClientListResponseDto, IClientListQueryDto>({
      query: params => ({
        url: '/system/client/list',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { data: IClientListResponseDto }) => response.data,
      providesTags: ['Clients'],
    }),

    getClientById: build.query<IClientApiDto, number>({
      query: id => ({
        url: `/system/client/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IClientApiDto }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Clients', id }],
    }),

    getLatestSupportSession: build.query<ILatestSupportSessionResponseDto, number>({
      query: companyId => ({
        url: '/system/support/sessions/latest',
        method: 'GET',
        params: { companyId },
      }),
      transformResponse: (response: { data: ILatestSupportSessionResponseDto }) => response.data,
      providesTags: (_result, _error, companyId) => [{ type: 'SupportSessions', id: companyId }],
    }),

    createSupportSession: build.mutation<ISupportSessionResponseDto, ICreateSupportSessionDto>({
      query: body => ({
        url: '/system/support/sessions',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: ISupportSessionResponseDto }) => response.data,
      invalidatesTags: (_result, _error, arg) => [{ type: 'SupportSessions', id: arg.companyId }],
    }),

    issueSupportCode: build.mutation<IIssueSupportCodeResponseDto, { sessionId: number; companyId: number }>({
      query: ({ sessionId }) => ({
        url: `/system/support/sessions/${sessionId}/codes`,
        method: 'POST',
      }),
      transformResponse: (response: { data: IIssueSupportCodeResponseDto }) => response.data,
      invalidatesTags: (_result, _error, arg) => [{ type: 'SupportSessions', id: arg.companyId }],
    }),

    terminateSupportSession: build.mutation<ISupportSessionDto, { sessionId: number; companyId: number }>({
      query: ({ sessionId }) => ({
        url: `/system/support/sessions/${sessionId}/terminate`,
        method: 'POST',
      }),
      transformResponse: (response: { data: ISupportSessionDto }) => response.data,
      invalidatesTags: (_result, _error, arg) => [{ type: 'SupportSessions', id: arg.companyId }],
    }),

    createClient: build.mutation<ICreateInvitationResponseDto, ICreateClientDto>({
      query: body => ({
        url: '/system/invitations/create',
        method: 'POST',
        body: {
          entityType: 'admin',
          entityId: 0,
          roleType: 'admin',
          channel: 'EMAIL',
          email: body.email,
          firstName: body.firstName,
          lastName: body.lastName,
          language: body.language,
          metadata: {
            source: 'owner-clients-modal',
            type: body.type,
            name: body.name,
            language: body.language,
          },
        },
      }),
      transformResponse: (response: { data: ICreateInvitationResponseDto }) => response.data,
    }),

    updateClient: build.mutation<IClientApiDto, { id: number; body: Partial<IClientApiDto> }>({
      query: ({ id, body }) => ({
        url: `/system/client/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { data: IClientApiDto }) => response.data,
      invalidatesTags: ['Clients'],
    }),

    removeClient: build.mutation<void, number>({
      query: id => ({
        url: `/system/client/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Clients'],
    }),

    resendInvitation: build.mutation<{ message: string }, { id: string; language?: string }>({
      query: ({ id, language }) => ({
        url: `/invitations/${id}/resend`,
        method: 'POST',
        body: { language },
      }),
    }),

    revokeInvitation: build.mutation<{ message: string }, string>({
      query: id => ({
        url: `/invitations/${id}/revoke`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Invitations'],
    }),

    deleteInvitation: build.mutation<{ message: string }, string>({
      query: id => ({
        url: `/invitations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitations'],
    }),
  }),
});

export const {
  useGetClientListQuery,
  useGetClientByIdQuery,
  useGetLatestSupportSessionQuery,
  useCreateSupportSessionMutation,
  useIssueSupportCodeMutation,
  useTerminateSupportSessionMutation,
  useCreateClientMutation,
  useUpdateClientMutation,
  useRemoveClientMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useDeleteInvitationMutation,
} = clientsApi;
